import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { FaEnvelope } from "react-icons/fa";
import FullPageLoader from "../components/FullPageLoader";
import api from "../services/api";
import "react-toastify/dist/ReactToastify.css";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');
.auth-page {
  min-height: 100vh; background: #f9f7f4;
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; padding: 24px 16px;
  position: relative; overflow: hidden;
}
.auth-blob { position: absolute; border-radius: 50%; pointer-events: none; z-index: 0; }
.auth-blob-1 { width: 420px; height: 420px; background: radial-gradient(circle, rgba(26,92,56,0.08) 0%, transparent 70%); top: -100px; left: -100px; }
.auth-blob-2 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(26,92,56,0.06) 0%, transparent 70%); bottom: -60px; right: -60px; }
.auth-card {
  background: #fff; border-radius: 24px; border: 1px solid #e8e4df;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08); padding: 44px 40px;
  width: 100%; max-width: 420px; position: relative; z-index: 1;
}
.auth-logo { display: flex; justify-content: center; margin-bottom: 8px; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-title { font-family: 'Playfair Display', serif; font-size: 1.75rem; font-weight: 700; color: #1a1a1a; text-align: center; margin-bottom: 6px; }
.auth-subtitle { font-size: 0.88rem; color: #8a8a8a; text-align: center; margin-bottom: 28px; line-height: 1.5; }
.af-label { display: block; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #1a5c38; margin-bottom: 6px; }
.af-input-wrap { position: relative; margin-bottom: 16px; }
.af-input {
  width: 100%; border: 1.5px solid #e8e4df; border-radius: 11px;
  padding: 11px 14px 11px 42px; font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem; color: #1a1a1a; background: #fff; outline: none;
  transition: border-color 0.18s; box-sizing: border-box;
}
.af-input:focus { border-color: #1a5c38; }
.af-input::placeholder { color: #bbb; }
.af-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #aaa; font-size: 0.9rem; pointer-events: none; }
.af-hint { font-size: 0.78rem; color: #aaa; margin-top: -8px; margin-bottom: 16px; }
.af-error { background: #fef2f2; border: 1px solid #fcd5d5; border-radius: 9px; padding: 10px 14px; font-size: 0.84rem; color: #d94f3d; margin-bottom: 14px; }
.af-success { background: #e8f3ed; border: 1px solid #b8dfc8; border-radius: 9px; padding: 10px 14px; font-size: 0.84rem; color: #1a5c38; margin-bottom: 14px; }
.af-submit {
  width: 100%; background: #1a5c38; color: #fff; border: none; border-radius: 12px; padding: 14px;
  font-family: 'DM Sans', sans-serif; font-size: 0.96rem; font-weight: 700; cursor: pointer;
  transition: background 0.18s, transform 0.14s; display: flex; align-items: center; justify-content: center; gap: 8px;
}
.af-submit:hover:not(:disabled) { background: #134429; transform: translateY(-1px); }
.af-submit:disabled { background: #a0c4b0; cursor: not-allowed; }
.af-spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: afspin 0.7s linear infinite; }
@keyframes afspin { to { transform: rotate(360deg); } }
.af-back { text-align: center; font-size: 0.88rem; color: #8a8a8a; margin-top: 20px; }
.af-back a { color: #1a5c38; font-weight: 600; text-decoration: none; }
.af-back a:hover { opacity: 0.75; }
@media (max-width: 480px) { .auth-card { padding: 32px 20px; border-radius: 18px; } .auth-title { font-size: 1.5rem; } }
`;

const ForgotPassword = () => {
  const [email,   setEmail]   = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await api.post("/users/forgot-password", { email: email.trim() });
      toast.success(response.data.message || t("auth.resetSent"));
      setTimeout(() => navigate("/login", { replace: true, state: { message: t("auth.resetCheckEmail") } }), 3000);
    } catch (err) {
      const msg = err.response?.data?.message || t("auth.resetSendFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{S}</style>
      {loading && <FullPageLoader text={t("auth.sendingReset")} />}
      <div className="auth-page">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-card">
          <div className="auth-logo"><img src="/images/Logo1.png" alt="FMart" /></div>
          <h1 className="auth-title">{t("auth.forgotPassword")}</h1>
          <p className="auth-subtitle">{t("auth.forgotSubtitle")}</p>

          <form onSubmit={handleSubmit}>
            <span className="af-label">{t("auth.emailAddress")}</span>
            <div className="af-input-wrap">
              <FaEnvelope className="af-input-icon" />
              <input className="af-input" type="email" placeholder="your@email.com"
                value={email} onChange={e => setEmail(e.target.value)}
                disabled={loading} required />
            </div>
            <p className="af-hint">{t("auth.resetHint")}</p>

            {error && <div className="af-error">{error}</div>}

            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? <><div className="af-spinner" /> {t("auth.sending")}</> : t("auth.sendResetLink")}
            </button>
          </form>

          <div className="af-back">
            {t("auth.rememberPassword")} <Link to="/login">{t("auth.backToLogin")}</Link>
          </div>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </>
  );
};

export default ForgotPassword;